import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  LayoutDashboard, Users, Heart, FileText, LogOut, 
  Home, Building, MessageSquare, TrendingUp, Clock,
  Menu, X, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin Layout Component - Mobile-first responsive design
export const AdminLayout = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const userData = localStorage.getItem("admin_user");
    
    if (!token) {
      navigate("/admin");
      return;
    }
    
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    toast.success("Logged out");
    navigate("/admin");
  };

  const navItems = [
    { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/leads", label: "Leads", icon: Users },
    { to: "/admin/prayers", label: "Prayer Requests", icon: Heart },
    { to: "/admin/audit", label: "Audit Trail", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#18181B] text-white h-14 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 hover:bg-white/10 rounded"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <span className="text-sm font-medium tracking-wide">STEWARD CONSOLE</span>
        <div className="w-9" /> {/* Spacer for centering */}
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 z-50 w-64 bg-[#18181B] text-white
        transform transition-transform duration-200 ease-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
          <span className="text-sm font-medium tracking-widest text-[#A1A1AA]">
            STEWARD CONSOLE
          </span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 hover:bg-white/10 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 flex-1">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = currentPage === item.to;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors
                      ${isActive 
                        ? "bg-white/10 text-white" 
                        : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4" strokeWidth={2} />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User & Logout */}
        <div className="p-3 border-t border-white/10">
          {user && (
            <div className="px-3 py-2 mb-2">
              <p className="text-sm font-medium text-white">{user.username}</p>
              <p className="text-xs text-[#71717A]">Administrator</p>
            </div>
          )}
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={2} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        {children}
      </main>
    </div>
  );
};

// Stat Card Component - Flat, muted, utilitarian
const StatCard = ({ label, value, subtext }) => {
  return (
    <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
      <p className="text-xs font-medium text-[#71717A] uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl sm:text-3xl font-semibold text-[#18181B] tabular-nums">{value}</p>
      {subtext && <p className="text-xs text-[#A1A1AA] mt-1">{subtext}</p>}
    </div>
  );
};

// Dashboard Page
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/leads?limit=5`, { headers })
      ]);

      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.leads);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getLeadTypeLabel = (type) => {
    switch (type) {
      case "seller_property": return "Seller";
      case "builder_request": return "Builder Request";
      case "builder_buybox": return "Buy Box";
      case "contact": return "Contact";
      default: return type;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      reviewed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      archived: "bg-zinc-100 text-zinc-600 border-zinc-200"
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <AdminLayout currentPage="/admin/dashboard">
        <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[50vh]">
          <p className="text-[#71717A] text-sm">Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="/admin/dashboard">
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-[#18181B]">Overview</h1>
          <p className="text-sm text-[#71717A] mt-1">Monitor leads, inquiries, and prayer requests</p>
        </div>

        {/* Stats Grid - Responsive */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <StatCard 
              label="Total Leads" 
              value={stats.leads.total}
            />
            <StatCard 
              label="Pending Review" 
              value={stats.leads.by_status.pending}
            />
            <StatCard 
              label="Seller Submissions" 
              value={stats.leads.by_type.seller_property}
            />
            <StatCard 
              label="Prayer Requests" 
              value={stats.prayer_requests.total}
            />
          </div>
        )}

        {/* Summary Sections - Responsive Grid */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Leads by Type */}
            <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
              <h2 className="text-sm font-medium text-[#18181B] mb-4">Leads by Type</h2>
              <div className="space-y-3">
                {Object.entries(stats.leads.by_type).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-[#52525B]">{getLeadTypeLabel(type)}</span>
                    <span className="font-medium text-[#18181B] tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Leads by Status */}
            <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
              <h2 className="text-sm font-medium text-[#18181B] mb-4">Leads by Status</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span className="text-[#52525B]">Pending</span>
                  </span>
                  <span className="font-medium text-[#18181B] tabular-nums">{stats.leads.by_status.pending}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span className="text-[#52525B]">Reviewed</span>
                  </span>
                  <span className="font-medium text-[#18181B] tabular-nums">{stats.leads.by_status.reviewed}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-zinc-400"></span>
                    <span className="text-[#52525B]">Archived</span>
                  </span>
                  <span className="font-medium text-[#18181B] tabular-nums">{stats.leads.by_status.archived}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Leads - Mobile Card View */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#E4E4E7]">
            <h2 className="text-sm font-medium text-[#18181B]">Recent Leads</h2>
            <Link to="/admin/leads" className="text-xs font-medium text-[#52525B] hover:text-[#18181B] flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          
          {/* Desktop Table - Hidden on mobile */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#71717A] uppercase tracking-wide">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-[#E4E4E7] last:border-0 hover:bg-[#FAFAFA]">
                    <td className="py-3 px-4 text-sm text-[#52525B]">{getLeadTypeLabel(lead.type)}</td>
                    <td className="py-3 px-4 text-sm font-medium text-[#18181B]">{lead.name}</td>
                    <td className="py-3 px-4 text-sm text-[#71717A]">{lead.email}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${getStatusBadge(lead.status)}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#A1A1AA] tabular-nums">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="sm:hidden divide-y divide-[#E4E4E7]">
            {recentLeads.map((lead) => (
              <div key={lead.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-[#18181B]">{lead.name}</p>
                    <p className="text-xs text-[#71717A]">{lead.email}</p>
                  </div>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${getStatusBadge(lead.status)}`}>
                    {lead.status}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
                  <span>{getLeadTypeLabel(lead.type)}</span>
                  <span>{new Date(lead.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
